import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Plus,
  Search,
  SlidersHorizontal,
  Upload,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import Card from "src/components/common/card/Card";
import InfoIcon from "src/components/ui/InfoIcon";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import BulkImportModal, {
  BulkImportColumn,
} from "src/components/bulk-import/BulkImportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as stockActions from "src/redux/slices/stockSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { formatVariantLabel } from "src/utils/common";
import stockApis from "src/api/stock";

const STATUS_BADGE: Record<string, { text: string; color: "green" | "yellow" | "red" }> = {
  in_stock: { text: "In stock", color: "green" },
  low_stock: { text: "Low stock", color: "yellow" },
  out_of_stock: { text: "Out of stock", color: "red" },
};

const OPENING_STOCK_IMPORT_COLUMNS: BulkImportColumn[] = [
  { key: "productName", label: "Product Name", example: "Wireless Mouse", aliases: ["product", "name"] },
  {
    key: "sku",
    label: "SKU",
    example: "",
    aliases: ["sku"],
  },
  { key: "warehouseName", label: "Warehouse", required: true, example: "Main Warehouse", aliases: ["warehouse"] },
  { key: "quantity", label: "Quantity", required: true, example: 50 },
  { key: "reason", label: "Reason", example: "Opening stock" },
];

const StockLevelsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [limit, setLimit] = useState(10);

  const { stockLevelList } = useAppSelector((state) => state.stock);
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canAdjust =
    isTenantAdmin || (permissions?.stockAdjustment || []).includes("create");
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel, plural: warehouseLabelPlural } = useWarehouseLabel();

  const rows = stockLevelList?.data?.data || [];

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, warehouseId, limit]);

  const getList = (page: number) =>
    dispatch(
      stockActions.getStockLevelListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(warehouseId ? { warehouseId } : {}),
      })
    );

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setWarehouseId("");
  };
  const hasActiveFilters = !!(search || warehouseId);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  return (
    <DashboardLayout
      meta={{
        title: "Stock Levels | Invyntra",
        description: `Track stock levels across ${warehouseLabelPlural.toLowerCase()}.`,
        keywords: `stock levels, inventory, ${warehouseLabelPlural.toLowerCase()}, invyntra`,
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <ClipboardList size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Inventory
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Track stock levels across {warehouseLabelPlural.toLowerCase()}.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canAdjust && (
              <Button
                variant="outline"
                rounded="full"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={16} className="mr-1.5" />
                Bulk import opening stock
              </Button>
            )}
            {canAdjust && (
              <Button rounded="full" onClick={() => navigate("/app/stock/adjust")}>
                <Plus size={16} className="mr-1" />
                Add stock
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search SKU
                </label>
                <Input
                  placeholder="Search by SKU..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label={warehouseLabel}
                value={warehouseId}
                onChange={setWarehouseId}
                allLabel={`All ${warehouseLabelPlural.toLowerCase()}`}
                options={warehouseOptions}
                disabled={isWarehouseLocked}
                helperText={
                  isWarehouseLocked
                    ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                    : undefined
                }
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Stock table"
            description={
              warehouseId
                ? warehouseOptions.find((w: any) => w.value === warehouseId)?.label
                : `All ${warehouseLabelPlural.toLowerCase()}`
            }
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "warehouse",
                header: warehouseLabel,
                accessor: ({ row }) => (
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {typeof row.warehouseId === "object" ? row.warehouseId?.name : "-"}
                    </div>
                  </div>
                ),
              },
              {
                key: "product",
                header: "Product",
                width: "220px",
                accessor: ({ row }) => (
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {typeof row.productId === "object" ? row.productId?.name : "-"}
                  </div>
                ),
              },
              {
                key: "variant",
                header: "Variant",
                accessor: ({ row }) =>
                  row.variant?.optionValues ? (
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {formatVariantLabel(row.variant.optionValues)}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  ),
              },
              {
                key: "sku",
                header: "SKU",
                accessor: ({ row }) => row.sku || "-",
              },
              {
                key: "quantity",
                header: (
                  <span className="flex items-center gap-1">
                    Quantity
                    <InfoIcon info="Total units physically on hand at this warehouse, including any already reserved by pending sale orders." />
                  </span>
                ),
                accessor: ({ row }) => row.quantity ?? 0,
              },
              {
                key: "reserved",
                header: (
                  <span className="flex items-center gap-1">
                    Reserved
                    <InfoIcon info="Units allocated to pending sale orders at this warehouse. Not free to sell, transfer out, or remove until those orders are completed or cancelled." />
                  </span>
                ),
                accessor: ({ row }) => row.reserved ?? 0,
              },
              {
                key: "available",
                header: (
                  <span className="flex items-center gap-1">
                    Available
                    <InfoIcon info="Quantity minus Reserved — the most that can be sold, transferred out, or removed from this warehouse right now." />
                  </span>
                ),
                accessor: ({ row }) => (
                  <span className="font-medium">{row.available ?? 0}</span>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => {
                  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.in_stock;
                  return <Badge text={badge.text} color={badge.color} />;
                },
              },
              {
                key: "actions",
                header: "Action",
                accessor: ({ row }) =>
                  canAdjust ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate("/app/stock/adjust", {
                          state: {
                            warehouseId:
                              typeof row.warehouseId === "object"
                                ? row.warehouseId?._id
                                : row.warehouseId,
                            productId:
                              typeof row.productId === "object"
                                ? row.productId?._id
                                : row.productId,
                            variantId: row.variantId,
                          },
                        })
                      }
                    >
                      Update
                    </Button>
                  ) : (
                    "-"
                  ),
              },
            ]}
            data={rows}
            pagination={stockLevelList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={stockLevelList.loading}
          />
        </Card>
      </div>

      {showImportModal && (
        <BulkImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Bulk import opening stock"
          entityLabel="stock entries"
          columns={OPENING_STOCK_IMPORT_COLUMNS}
          sampleFileName="opening-stock-import-sample.xlsx"
          onImport={(rows) => stockApis.bulkImport(rows)}
          onImported={() => getList(1)}
          helperNote={
            <>
              Identify each product by <strong>Product Name</strong> or{" "}
              <strong>SKU</strong> — SKU is required if the product has
              variants (Size, Color, etc.), since Name alone can't tell
              which variant you mean. The <strong>Warehouse</strong> must
              already exist. This only adds stock — it can't be used to
              remove it.
            </>
          }
        />
      )}
    </DashboardLayout>
  );
};

export default StockLevelsPage;
